import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowRight,
  LogOut,
  Clock,
  Scissors,
  Package,
  Users,
  Building2,
  ChevronLeft,
  FileText,
  Award,
  Crown,
  CreditCard,
  Lock,
  Receipt,
  MessageSquareText,
  MessageCircle
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/auth/hooks';
import { useSubscription } from '../../hooks/useSubscription';
import { supabase } from '../../lib/supabase';

/** ===== עטיפת גלילה פנימית ===== */
function PageScroll({
  children,
  className = '',
  bottomPad = true,
}: {
  children: React.ReactNode;
  className?: string;
  bottomPad?: boolean;
}) {
  return (
    <div
      className={[
        'w-full',
        'overflow-y-auto overflow-x-hidden',
        'flex-1 min-h-0',
        bottomPad ? 'pb-[calc(var(--nav-height)+env(safe-area-inset-bottom)+8px)]' : '',
        className,
      ].join(' ')}
      style={{
        height: 'calc(100svh - (var(--header-height) + var(--safe-top)))',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {children}
    </div>
  );
}

const settingsGroups = [
  {
    title: 'הגדרות עסק',
    items: [
      {
        title: 'הגדרות עסק',
        description: 'פרטי העסק, לוגו וקישור חיצוני',
        icon: Building2,
        path: '/settings/business',
        iconColor: 'text-orange-600',
        requiredFeature: null
      },
      {
        title: 'שעות פעילות',
        description: 'הגדרת זמני פעילות העסק והפסקות',
        icon: Clock,
        path: '/settings/hours',
        iconColor: 'text-blue-600',
        requiredFeature: null
      },
      {
        title: 'שירותים',
        description: 'ניהול שירותי העסק ומחירים',
        icon: Scissors,
        path: '/settings/services',
        iconColor: 'text-emerald-600',
        requiredFeature: null
      },
      {
        title: 'מוצרים',
        description: 'ניהול מוצרי העסק ומלאי',
        icon: Package,
        path: '/settings/products',
        iconColor: 'text-rose-600',
        requiredFeature: null
      },
      {
        title: 'צוות',
        description: 'ניהול אנשי צוות והרשאות',
        icon: Users,
        path: '/settings/staff',
        iconColor: 'text-violet-600',
        requiredFeature: null
      }
    ]
  },
  {
    title: 'תכונות מתקדמות',
    items: [
      {
        title: 'תוכנית נאמנות',
        description: 'הגדרות נקודות ורמות נאמנות',
        icon: Award,
        path: '/settings/loyalty',
        iconColor: 'text-amber-600',
        requiredFeature: 'loyalty_program'
      },
      {
        title: 'סליקה וחשבוניות',
        description: 'ניהול סליקות וחשבוניות',
        icon: FileText,
        path: '/settings/invoices',
        iconColor: 'text-indigo-600',
        requiredFeature: 'invoices',
        requiredRole: 'ADMIN'
      }
    ]
  },
  {
    title: 'WhatsApp Business',
    items: [
      {
        title: 'הגדרות WhatsApp',
        description: 'תבניות, היסטוריה וסטטיסטיקות',
        icon: MessageSquareText,
        path: '/settings/whatsapp',
        iconColor: 'text-green-600',
        requiredFeature: null
      }
    ]
  },
  {
    title: 'מנוי וחיוב',
    items: [
      {
        title: 'ניהול מנוי',
        description: 'צפייה ועדכון החבילה שלך',
        icon: Crown,
        path: '/subscription',
        iconColor: 'text-yellow-600',
        requiredFeature: null
      },
      {
        title: 'שדרג חבילה',
        description: 'שנה לחבילה מתאימה יותר',
        icon: CreditCard,
        path: '/plan-upgrade',
        iconColor: 'text-green-600',
        requiredFeature: null
      }
    ]
  },
  {
    title: 'תמיכה ועזרה',
    items: [
      {
        title: 'תמיכה',
        description: 'פתח פנייה ועקוב אחרי טיקטים',
        icon: MessageCircle,
        path: '/support',
        iconColor: 'text-blue-600',
        requiredFeature: null
      }
    ]
  }
] as const;

type SettingsItem = typeof settingsGroups[number]['items'][number];

function Settings() {
  const navigate = useNavigate();
  const { signOut, user } = useAuth();
  const { subscription, loading: subscriptionLoading } = useSubscription();

  const metaRole: string | null = (user?.user_metadata?.role as string | undefined) ?? null;
  const [dbRole, setDbRole] = useState<string | null>(null);
  const [loadingRole, setLoadingRole] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!user?.id) return;
      setLoadingRole(true);
      const { data, error } = await supabase
        .from('users')
        .select('role')
        .eq('id', user.id)
        .single();

      if (cancelled) return;
      if (!error && data) {
        setDbRole((data?.role as string) ?? null);
      }
      setLoadingRole(false);
    };
    load();
    return () => { cancelled = true; };
  }, [user?.id]);

  const effectiveRole = useMemo<string | null>(() => {
    return metaRole || dbRole || null;
  }, [metaRole, dbRole]);

  const isFeatureAvailable = (featureCode: string | null): boolean => {
    if (!featureCode) return true;
    if (subscriptionLoading) return false;
    if (!subscription) return false;
    if (featureCode === 'loyalty_program') return true;
    return Boolean(subscription.plan?.features?.[featureCode] === true);
  };

  const hasRoleAccess = (requiredRole?: string): boolean => {
    if (!requiredRole) return true;
    const r = effectiveRole?.toLowerCase();
    return r ? r === requiredRole.toLowerCase() : false;
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login', { replace: true });
    } catch (error) {

    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-96">
        <span className="text-lg font-semibold text-gray-600">טוען משתמש...</span>
      </div>
    );
  }

  const renderMenuItem = (item: SettingsItem) => {
    const Icon = item.icon;
    const featureReady = isFeatureAvailable(item.requiredFeature);
    const roleReady = hasRoleAccess((item as any).requiredRole);
    const isDisabled = !featureReady || !roleReady;

    if (isDisabled) {
      return (
        <div
          key={item.path}
          className="flex items-center gap-3 px-4 py-3.5 bg-gray-50 opacity-50 cursor-not-allowed"
        >
          <div className={`p-2 rounded-lg bg-gray-100`}>
            <Icon className="h-5 w-5 text-gray-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-700">{item.title}</div>
            <div className="text-xs text-gray-500 truncate">{item.description}</div>
          </div>
          <Lock className="h-4 w-4 text-gray-400 flex-shrink-0" />
        </div>
      );
    }

    return (
      <Link
        key={item.path}
        to={item.path}
        className="flex items-center gap-3 px-4 py-3.5 active:bg-gray-50 transition-colors"
      >
        <div className={`p-2 rounded-lg ${item.iconColor} bg-opacity-10`}>
          <Icon className={`h-5 w-5 ${item.iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900">{item.title}</div>
          <div className="text-xs text-gray-500 truncate">{item.description}</div>
        </div>
        <ChevronLeft className="h-5 w-5 text-gray-400 flex-shrink-0" />
      </Link>
    );
  };

  return (
    <div className="h-full flex flex-col min-h-0 bg-gray-50">
      <PageScroll className="space-y-4">
        <div className="sticky top-0 z-10 bg-gray-50 px-4 pt-4 pb-2">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="text-gray-500 active:text-gray-700">
              <ArrowRight className="h-6 w-6" />
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">הגדרות</h1>
          </div>
        </div>

        {subscription && (
          <div className="px-4">
            <div className="bg-gradient-to-l from-blue-500 to-blue-600 rounded-xl p-4 text-white shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs opacity-90 mb-1">החבילה הנוכחית</div>
                  <div className="text-lg font-bold">{subscription.plan.name}</div>
                </div>
                <button
                  onClick={() => navigate('/plan-upgrade')}
                  className="px-4 py-2 bg-white text-blue-600 rounded-lg text-sm font-medium active:bg-gray-100 transition-colors"
                >
                  שדרג
                </button>
              </div>
            </div>
          </div>
        )}

        {settingsGroups.map((group, index) => (
          <div key={index} className="bg-white rounded-xl mx-4 overflow-hidden shadow-sm">
            <div className="px-4 py-2 border-b border-gray-100">
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {group.title}
              </h2>
            </div>
            <div className="divide-y divide-gray-100">
              {group.items.map(renderMenuItem)}
            </div>
          </div>
        ))}

        <div className="px-4 pb-6">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl bg-white text-red-600 active:bg-red-50 transition-colors shadow-sm border border-gray-100"
          >
            <LogOut className="h-5 w-5" />
            <span className="font-medium">התנתק</span>
          </button>
        </div>
      </PageScroll>
    </div>
  );
}

export default Settings;
